/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import React, { Fragment, memo, Suspense, useMemo } from 'react';

import { keyframes, cssMap as unboundCssMap } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { ReactRenderer, type RendererProps } from '@atlaskit/renderer';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';

import type { MarkdownElementType } from '../types';

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const animatedStyles = cssMap({
	container: {
		animation: `${fadeIn} 700ms cubic-bezier(0.3,0,0.8,0.15) forwards`,
	},
});

const unboundAnimatedStyles = unboundCssMap({
	container: {
		opacity: 0,
	},
	listItem: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > li::marker': {
			opacity: 0,
			animation: `${fadeIn} 700ms cubic-bezier(0.3,0,0.8,0.15) forwards`,
		},
	},
});

// Animated components for streaming
const StaggeredChild = memo(({ children, index }: { children: React.ReactNode; index: number }) => {
	// If children is a string longer than 10 characters, split it into 3 parts
	if (typeof children === 'string' && children.length > 10) {
		const chunkSize = Math.ceil(children.length / 3);
		const parts = [
			children.slice(0, chunkSize),
			children.slice(chunkSize, chunkSize * 2),
			children.slice(chunkSize * 2),
		];

		return (
			<>
				{parts.map((part, partIndex) => (
					<span
						key={partIndex}
						css={[animatedStyles.container, unboundAnimatedStyles.container]}
						style={{
							animationDelay: `${index * 200 + partIndex * 100}ms`,
						}}
					>
						{part}
					</span>
				))}
			</>
		);
	}

	// For non-strings or short strings, render normally
	return (
		<span
			css={[animatedStyles.container, unboundAnimatedStyles.container]}
			style={{
				animationDelay: `${index * 200}ms`,
			}}
		>
			{children}
		</span>
	);
});

StaggeredChild.displayName = 'StaggeredChild';

const AnimatedParagraph = memo(({ children }: { children: React.ReactNode }) => {
	const childrenArray = React.Children.toArray(children).filter((child) => {
		// Defensive: Filter out 0 (in case it leaks through from other code paths)
		// React.Children.toArray already filters out null, undefined, and false
		if (child === 0) {
			return false;
		}

		// Filter out empty strings
		return child !== '';
	});

	// If no valid children after filtering, return null
	if (childrenArray.length === 0) {
		return null;
	}

	return (
		<Box as="p">
			{childrenArray.map((child, index) => {
				// Use content-based keys to preserve component identity during streaming
				// This prevents re-animating existing content when new content is added
				const key = typeof child === 'string'
					? `${child.substring(0, 20)}-${index}` // Use content prefix + index
					: index;

				return (
					<StaggeredChild key={key} index={index}>
						{child}
					</StaggeredChild>
				);
			})}
		</Box>
	);
});

AnimatedParagraph.displayName = 'AnimatedParagraph';

const AnimatedOrderedList = memo(
	({ children, ...props }: { [key: string]: any; children: React.ReactNode; }) => {
		return (
			<ol
				{...props}
				css={[
					animatedStyles.container,
					unboundAnimatedStyles.container,
					unboundAnimatedStyles.listItem,
				]}
			>
				{children}
			</ol>
		);
	},
);

AnimatedOrderedList.displayName = 'AnimatedOrderedList';

const AnimatedBulletedList = memo(
	({ children, ...props }: { [key: string]: any; children: React.ReactNode; }) => {
		return (
			<ul
				{...props}
				css={[
					animatedStyles.container,
					unboundAnimatedStyles.container,
					unboundAnimatedStyles.listItem,
				]}
			>
				{children}
			</ul>
		);
	},
);

AnimatedBulletedList.displayName = 'AnimatedBulletedList';

const AnimatedHeading = memo(
	({ children, ...props }: { [key: string]: any; children: React.ReactNode; }) => {
		const headingLevel = props.level || 1;
		const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;

		return (
			<HeadingTag {...props} css={[animatedStyles.container, unboundAnimatedStyles.container]}>
				{children}
			</HeadingTag>
		);
	},
);

AnimatedHeading.displayName = 'AnimatedHeading';

// Node components for ReactRenderer
const animatedNodeComponents = {
	paragraph: AnimatedParagraph,
	orderedList: AnimatedOrderedList,
	bulletList: AnimatedBulletedList,
	heading: AnimatedHeading,
};

export const MarkdownElement = React.memo(
	({
		component,
		isStreaming = false,
	}: {
		component: MarkdownElementType;
		isStreaming?: boolean;
	}): React.JSX.Element | null => {
		const markdown = component.children?.[0] as string;

		// Use streaming flag to control Smart Links processing
		const docContent = useMemo(
			() => (markdown ? encodeAdf(markdown) : undefined),
			[markdown],
		);

		// Memoize renderer config
		const rendererConfig: RendererProps | undefined = useMemo(() => {
			if (!docContent) {
				return undefined;
			}

			return {
				document: docContent,
				nodeComponents: animatedNodeComponents,
				// Enable smart links
				smartLinks: {
					ssr: true,
				},
			};
		}, [docContent]);

		if (!rendererConfig) {
			return null;
		}

		return (
			<Box testId={isStreaming ? 'markdown-element-streaming' : 'markdown-element'}>
				<Suspense fallback={<Fragment />}>
					<ReactRenderer {...rendererConfig} />
				</Suspense>
			</Box>
		);
	},
);
